import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ILoginModel,
  ILoginViaGoogleModel,
  ILoginViaLinkedInModel,
  IRegisterAsJournalistModel,
  IUser,
} from '../../api/base/Auth/types';
import { AuthApi } from '../../api/base/Auth';
import { IValidationErrors } from '../../api/base/baseApiTypes';
import { startMainLoading, stopMainLoading } from 'src/store/layout/slice';
import { LocalStorageApi } from '../../api/localStorage';
import { api } from '../../api/base';

export interface IValidationErrorResponse {
  message: string;
  errors: IValidationErrors;
}

export function setAuthTokenInSystem(
  token: string,
  params: { setInLocalStorage?: boolean } = {},
): void {
  const { setInLocalStorage = true } = params;
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
  if (setInLocalStorage) {
    LocalStorageApi.setAccessToken(token);
  }
}

export const fetchLoginViaPassword = createAsyncThunk<
  // Return type of the payload creator
  IUser,
  // First argument to the payload creator
  ILoginModel,
  {
    rejectValue: any;
  }
>('login/password', async (userData, thunkApi) => {
  try {
    const response = await AuthApi.login(userData);
    setAuthTokenInSystem(response.data.access_token);
    return response.data.user;
  } catch (error) {
    return thunkApi.rejectWithValue({
      data: error.response.data,
      statusCode: error.response.status,
    });
  }
});

export const fetchLoginViaGoogle = createAsyncThunk<
  IUser,
  ILoginViaGoogleModel,
  {
    rejectValue: any;
  }
>('login/google', async (userData, thunkApi) => {
  thunkApi.dispatch(startMainLoading());
  try {
    const response = await AuthApi.loginViaGoogle(userData);
    setAuthTokenInSystem(response.data.access_token);
    return response.data.user;
  } catch (error) {
    return thunkApi.rejectWithValue({
      data: error.response.data,
      statusCode: error.response.status,
    });
  } finally {
    thunkApi.dispatch(stopMainLoading());
  }
});

export const fetchLoginViaLinkedIn = createAsyncThunk<
  IUser,
  ILoginViaLinkedInModel,
  {
    rejectValue: any;
  }
>('login/linkedIn', async (userData, thunkApi) => {
  thunkApi.dispatch(startMainLoading());
  try {
    const response = await AuthApi.loginViaLinkedIn(userData);
    setAuthTokenInSystem(response.data.access_token);
    return response.data.user;
  } catch (error) {
    return thunkApi.rejectWithValue({
      data: error.response.data,
      statusCode: error.response.status,
    });
  } finally {
    thunkApi.dispatch(stopMainLoading());
  }
});

export const getProfile = createAsyncThunk<
  // Return type of the payload creator
  IUser,
  // First argument to the payload creator
  void,
  {
    rejectValue: any;
  }
>('login/getProfile', async (_userData, thunkApi) => {
  try {
    thunkApi.dispatch(startMainLoading());
    setAuthTokenInSystem(LocalStorageApi.getAccessToken(), {
      setInLocalStorage: false,
    });
    const response = await AuthApi.getProfile();
    return response.data.data;
  } catch (error) {
    return thunkApi.rejectWithValue({
      data: error.response.data,
      statusCode: error.response.status,
    });
  } finally {
    thunkApi.dispatch(stopMainLoading());
  }
});

export const fetchRegisterAsJournalist = createAsyncThunk<
  // Return type of the payload creator
  IUser,
  // First argument to the payload creator
  IRegisterAsJournalistModel,
  {
    rejectValue: any;
  }
>('register/journalist', async (userData, thunkApi) => {
  try {
    const response = await AuthApi.registerAsJournalist(userData);
    setAuthTokenInSystem(response.data.access_token);
    return response.data.user;
  } catch (error) {
    return thunkApi.rejectWithValue({
      data: error.response.data,
      statusCode: error.response.status,
    });
  }
});
